// @core/stores/tenant/TenantTenantStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { tenantApi } from '@core/api';
import type {
  CreateTenantRequest,
  Tenant,
  UpdateTenantRequest,
} from '@core/types/tenant';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TenantTenantStore {
  // List state
  items: Tenant[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Tenant | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Tenant>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Tenant | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Tenant | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create a new tenant
   */
  async CreateTenant(data: CreateTenantRequest): Promise<Tenant> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await tenantApi.CreateTenant(data);
      const item = response.tenant;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete tenant
   */
  async DeleteTenant(tenant_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await tenantApi.DeleteTenant(tenant_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== tenant_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === tenant_id) {
          this.currentItem = null;
        }

        this.removeFromCache(tenant_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant by ID (respects privacy settings)
   */
  async GetTenant(tenant_id: string): Promise<Tenant> {
    // Check cache first
    const cached = this.cache.get(tenant_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await tenantApi.GetTenant(tenant_id);
      const item = response.tenant;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List tenants (respects privacy settings)
   */
  async ListTenants(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await tenantApi.ListTenants(params);

      runInAction(() => {
        this.items = response.tenants?.tenants ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant
   */
  async UpdateTenant(tenant_id: string, data: UpdateTenantRequest): Promise<Tenant> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await tenantApi.UpdateTenant(tenant_id, data);
      const item = response.tenant;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const tenantTenantStore = new TenantTenantStore();
